import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Search,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Globe,
  Trash2,
  Edit,
  Eye,
  Loader2,
  RefreshCw,
  Award,
  Target,
  Zap,
  ExternalLink
} from 'lucide-react';
import {
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const platformIcons = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: Twitter,
  youtube: Youtube
};

const CompetitorsPage = () => {
  const [competitors, setCompetitors] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [industryBenchmarks, setIndustryBenchmarks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [competitorHistory, setCompetitorHistory] = useState([]);
  const [contentAnalysis, setContentAnalysis] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [contentGaps, setContentGaps] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState({
    name: '',
    industry: '',
    website: '',
    social_handles: {},
    notes: ''
  });
  const [selectedIndustry, setSelectedIndustry] = useState('technology');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [compRes, compareRes] = await Promise.all([
        api.get('/competitors/'),
        api.get('/competitors/benchmark/compare')
      ]);
      setCompetitors(compRes.data);
      setComparison(compareRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadIndustryBenchmarks = async (industry) => {
    try {
      const response = await api.get(`/competitors/benchmark/industry/${industry}`);
      setIndustryBenchmarks(response.data);
    } catch (error) {
      toast.error('Failed to load benchmarks');
    }
  };

  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const [recsRes, gapsRes] = await Promise.all([
        api.get('/competitors/insights/recommendations'),
        api.get('/competitors/insights/gaps')
      ]);
      setRecommendations(recsRes.data);
      setContentGaps(gapsRes.data);
    } catch (error) {
      console.error('Failed to load insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  };

  const loadContentAnalysis = async (competitorId) => {
    try {
      const res = await api.get(`/competitors/${competitorId}/content-analysis`);
      setContentAnalysis(res.data);
    } catch (error) {
      console.error('Failed to load content analysis:', error);
    }
  };

  const handleAddCompetitor = async () => {
    if (!newCompetitor.name) {
      toast.error('Please enter competitor name');
      return;
    }

    try {
      await api.post('/competitors/', newCompetitor);
      toast.success('Competitor added!');
      setShowAddDialog(false);
      setNewCompetitor({ name: '', industry: '', website: '', social_handles: {}, notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to add competitor');
    }
  };

  const handleDeleteCompetitor = async (competitorId) => {
    if (!confirm('Are you sure you want to remove this competitor?')) return;

    try {
      await api.delete(`/competitors/${competitorId}`);
      toast.success('Competitor removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove competitor');
    }
  };

  const viewCompetitorDetails = async (competitor) => {
    setSelectedCompetitor(competitor);
    setShowDetailsDialog(true);
    setContentAnalysis(null);
    
    try {
      const [detailsRes, historyRes] = await Promise.all([
        api.get(`/competitors/${competitor.competitor_id}`),
        api.get(`/competitors/${competitor.competitor_id}/history?days=30`)
      ]);
      setSelectedCompetitor(detailsRes.data);
      setCompetitorHistory(historyRes.data);
      
      // Load content analysis
      loadContentAnalysis(competitor.competitor_id);
    } catch (error) {
      console.error('Error loading details:', error);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Competitor Benchmarking</h1>
            <p className="text-slate-500 mt-1">Track and compare your performance against competitors</p>
          </div>
          <Button onClick={() => setShowAddDialog(true)} className="gap-2" data-testid="add-competitor-btn">
            <Plus className="w-4 h-4" />
            Add Competitor
          </Button>
        </div>

        <Tabs defaultValue="competitors" className="space-y-6">
          <TabsList>
            <TabsTrigger value="competitors" data-testid="tab-competitors">Competitors</TabsTrigger>
            <TabsTrigger value="comparison" data-testid="tab-comparison">Comparison</TabsTrigger>
            <TabsTrigger value="insights" data-testid="tab-insights">Insights</TabsTrigger>
            <TabsTrigger value="benchmarks" data-testid="tab-benchmarks">Industry Benchmarks</TabsTrigger>
          </TabsList>

          {/* Competitors Tab */}
          <TabsContent value="competitors" className="space-y-4">
            {competitors.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-medium text-slate-700 mb-1">No competitors tracked yet</h3>
                  <p className="text-sm text-slate-500 mb-4">Add competitors to start benchmarking your performance</p>
                  <Button onClick={() => setShowAddDialog(true)} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Add Your First Competitor
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {competitors.map(comp => (
                  <Card key={comp.competitor_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold">{comp.name}</h3>
                          {comp.industry && (
                            <Badge variant="secondary" className="text-xs mt-1">
                              {comp.industry}
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8"
                            onClick={() => viewCompetitorDetails(comp)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8"
                            onClick={() => handleDeleteCompetitor(comp.competitor_id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>

                      {/* Social handles */}
                      <div className="flex gap-2 flex-wrap mb-3">
                        {Object.entries(comp.social_handles || {}).map(([platform, handle]) => {
                          const Icon = platformIcons[platform] || Globe;
                          return (
                            <div key={platform} className="flex items-center gap-1 text-xs text-slate-600">
                              <Icon className="w-3.5 h-3.5" />
                              <span>@{handle}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Quick metrics */}
                      <div className="grid grid-cols-2 gap-2 pt-3 border-t">
                        {Object.entries(comp.metrics || {}).slice(0, 2).map(([platform, metrics]) => (
                          <div key={platform} className="text-center">
                            <p className="text-xs text-slate-500 capitalize">{platform}</p>
                            <p className="font-semibold text-sm">{formatNumber(metrics?.followers)}</p>
                            <p className="text-xs text-slate-400">followers</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Comparison Tab */}
          <TabsContent value="comparison" className="space-y-6">
            {comparison && (
              <>
                {/* Insights */}
                {comparison.insights?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Zap className="w-5 h-5 text-yellow-500" />
                        Key Insights
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {comparison.insights.map((insight, i) => (
                          <div 
                            key={i}
                            className={`p-3 rounded-lg flex items-start gap-3 ${
                              insight.type === 'positive' 
                                ? 'bg-green-50 text-green-700' 
                                : 'bg-amber-50 text-amber-700'
                            }`}
                          >
                            {insight.type === 'positive' ? (
                              <TrendingUp className="w-5 h-5 mt-0.5" />
                            ) : (
                              <Target className="w-5 h-5 mt-0.5" />
                            )}
                            <p className="text-sm">{insight.message}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Comparison Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Engagement Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={[
                          { 
                            name: 'You', 
                            ...Object.fromEntries(
                              Object.entries(comparison.your_metrics?.platforms || {}).map(([p, m]) => [p, m?.avg_engagement || m?.posts || 0])
                            )
                          },
                          ...(comparison.competitors || []).map(c => ({
                            name: c.name,
                            ...Object.fromEntries(
                              Object.entries(c.platforms || {}).map(([p, m]) => [p, m?.avg_likes || m?.avg_engagement || 0])
                            )
                          }))
                        ]}
                      >
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="instagram" fill="#E1306C" name="Instagram" />
                        <Bar dataKey="facebook" fill="#1877F2" name="Facebook" />
                        <Bar dataKey="twitter" fill="#1DA1F2" name="Twitter" />
                        <Bar dataKey="linkedin" fill="#0A66C2" name="LinkedIn" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" className="space-y-6" data-testid="insights-tab">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">Content Strategy Insights</h2>
                <p className="text-sm text-slate-500">Recommendations based on competitor analysis</p>
              </div>
              <Button 
                variant="outline" 
                onClick={loadInsights}
                disabled={insightsLoading}
              >
                {insightsLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh Insights
              </Button>
            </div>

            {insightsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Recommendations */}
                <Card className="border-slate-100">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                      Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {recommendations?.recommendations?.length > 0 ? (
                      <div className="space-y-3">
                        {recommendations.recommendations.map((rec, i) => (
                          <div 
                            key={i} 
                            className={`p-3 rounded-lg border ${
                              rec.priority === 'high' 
                                ? 'border-red-200 bg-red-50' 
                                : rec.priority === 'medium'
                                  ? 'border-amber-200 bg-amber-50'
                                  : 'border-slate-200 bg-slate-50'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="font-medium text-sm">{rec.title}</h4>
                              <Badge variant="outline" className={`text-xs ${
                                rec.priority === 'high' ? 'border-red-300 text-red-700' :
                                rec.priority === 'medium' ? 'border-amber-300 text-amber-700' :
                                'border-slate-300 text-slate-700'
                              }`}>
                                {rec.priority}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-600 mb-2">{rec.description}</p>
                            <div className="flex items-center gap-1 text-xs text-emerald-600">
                              <Target className="w-3 h-3" />
                              <span>{rec.action}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-slate-500">
                        <Target className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm">Click "Refresh Insights" to get recommendations</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Content Gaps */}
                <Card className="border-slate-100">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Search className="w-5 h-5 text-blue-500" />
                      Content Gaps
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {contentGaps?.gaps?.length > 0 ? (
                      <div className="space-y-3">
                        {contentGaps.gaps.map((gap, i) => (
                          <div key={i} className="p-3 rounded-lg border border-blue-200 bg-blue-50">
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="font-medium text-sm">{gap.title}</h4>
                              <Badge variant="outline" className={`text-xs ${
                                gap.impact === 'high' ? 'border-red-300 text-red-700' :
                                gap.impact === 'medium' ? 'border-amber-300 text-amber-700' :
                                'border-slate-300 text-slate-700'
                              }`}>
                                {gap.impact} impact
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-600 mb-2">{gap.description}</p>
                            <div className="flex items-center gap-1 text-xs text-blue-600">
                              <Zap className="w-3 h-3" />
                              <span>{gap.opportunity}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-slate-500">
                        <Search className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm">Click "Refresh Insights" to identify content gaps</p>
                      </div>
                    )}

                    {contentGaps?.analysis_summary && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <p className="text-xs text-slate-500">
                          Based on {contentGaps.analysis_summary.your_posts} posts and {contentGaps.analysis_summary.competitors_tracked} competitors
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Industry Benchmarks Tab */}
          <TabsContent value="benchmarks" className="space-y-6">
            <div className="flex items-center gap-4 mb-4">
              <Label>Select Industry:</Label>
              <Select 
                value={selectedIndustry} 
                onValueChange={(v) => {
                  setSelectedIndustry(v);
                  loadIndustryBenchmarks(v);
                }}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technology">Technology</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="healthcare">Healthcare</SelectItem>
                  <SelectItem value="food">Food & Beverage</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="fitness">Fitness</SelectItem>
                  <SelectItem value="education">Education</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => loadIndustryBenchmarks(selectedIndustry)}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Load Benchmarks
              </Button>
            </div>

            {industryBenchmarks && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base capitalize flex items-center gap-2">
                      <Award className="w-5 h-5 text-amber-500" />
                      {industryBenchmarks.industry} Industry Standards
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-emerald-600">
                          {industryBenchmarks.avg_engagement_rate}%
                        </p>
                        <p className="text-xs text-slate-500">Avg Engagement Rate</p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-lg font-semibold text-slate-700">
                          {industryBenchmarks.avg_posting_frequency}
                        </p>
                        <p className="text-xs text-slate-500">Posting Frequency</p>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium mb-2">Top Platforms</p>
                      <div className="flex gap-2 flex-wrap">
                        {industryBenchmarks.top_platforms?.map(p => {
                          const Icon = platformIcons[p] || Globe;
                          return (
                            <Badge key={p} variant="secondary" className="gap-1">
                              <Icon className="w-3 h-3" />
                              {p}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium mb-2">Best Posting Times</p>
                      <div className="flex gap-2 flex-wrap">
                        {industryBenchmarks.best_posting_times?.map(time => (
                          <Badge key={time} variant="outline">{time}</Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Recommended Content Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {industryBenchmarks.content_types?.map((type, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                          <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-medium">
                            {i + 1}
                          </div>
                          <span className="text-sm">{type}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Add Competitor Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Competitor</DialogTitle>
              <DialogDescription>
                Track a competitor's social media performance
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Competitor Name *</Label>
                <Input
                  value={newCompetitor.name}
                  onChange={(e) => setNewCompetitor({ ...newCompetitor, name: e.target.value })}
                  placeholder="e.g., Acme Corp"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Industry</Label>
                <Select
                  value={newCompetitor.industry}
                  onValueChange={(v) => setNewCompetitor({ ...newCompetitor, industry: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technology">Technology</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="food">Food & Beverage</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="fitness">Fitness</SelectItem>
                    <SelectItem value="education">Education</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={newCompetitor.website}
                  onChange={(e) => setNewCompetitor({ ...newCompetitor, website: e.target.value })}
                  placeholder="https://example.com"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Social Media Handles</Label>
                <div className="space-y-2 mt-2">
                  {['instagram', 'twitter', 'facebook', 'linkedin', 'youtube'].map(platform => {
                    const Icon = platformIcons[platform] || Globe;
                    return (
                      <div key={platform} className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-slate-400" />
                        <Input
                          placeholder={`@${platform}_handle`}
                          value={newCompetitor.social_handles[platform] || ''}
                          onChange={(e) => setNewCompetitor({
                            ...newCompetitor,
                            social_handles: { ...newCompetitor.social_handles, [platform]: e.target.value }
                          })}
                          className="flex-1"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddCompetitor}>
                  Add Competitor
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Competitor Details Dialog */}
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedCompetitor?.name}</DialogTitle>
              <DialogDescription>
                {selectedCompetitor?.website && (
                  <a href={selectedCompetitor.website} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline flex items-center gap-1">
                    {selectedCompetitor.website} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </DialogDescription>
            </DialogHeader>
            
            {selectedCompetitor && (
              <div className="space-y-6">
                {/* Metrics by Platform */}
                <div>
                  <h4 className="font-medium mb-3">Performance by Platform</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(selectedCompetitor.metrics || {}).map(([platform, metrics]) => {
                      const Icon = platformIcons[platform] || Globe;
                      return (
                        <Card key={platform}>
                          <CardContent className="p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <Icon className="w-4 h-4" />
                              <span className="font-medium capitalize">{platform}</span>
                              <Badge variant="outline" className="text-[10px] ml-auto">
                                {metrics?.data_source || 'estimated'}
                              </Badge>
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Followers</span>
                                <span className="font-medium">{formatNumber(metrics?.followers)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Engagement</span>
                                <span className="font-medium">{metrics?.engagement_rate}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Avg Likes</span>
                                <span className="font-medium">{formatNumber(metrics?.avg_likes)}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>

                {/* Historical Chart */}
                {competitorHistory.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3">30-Day Follower Trend</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={competitorHistory.map(d => ({
                        date: d.date,
                        ...Object.fromEntries(
                          Object.entries(d.platforms || {}).map(([p, m]) => [p, m?.followers || 0])
                        )
                      }))}>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {Object.keys(selectedCompetitor.social_handles || {}).map((platform, i) => (
                          <Line 
                            key={platform}
                            type="monotone" 
                            dataKey={platform} 
                            stroke={['#E1306C', '#1877F2', '#1DA1F2', '#0A66C2', '#FF0000'][i % 5]}
                            strokeWidth={2}
                            dot={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Content Analysis */}
                {contentAnalysis && (
                  <div className="space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-purple-500" />
                      Content Strategy Analysis
                    </h4>
                    
                    {/* Content Types */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500 mb-2">Content Mix</p>
                        <div className="space-y-2">
                          {contentAnalysis.content_types && Object.entries(contentAnalysis.content_types).map(([type, percent]) => (
                            <div key={type} className="flex items-center gap-2">
                              <div className="flex-1 bg-slate-100 rounded-full h-2">
                                <div 
                                  className="bg-emerald-500 rounded-full h-2" 
                                  style={{ width: `${percent}%` }}
                                />
                              </div>
                              <span className="text-xs text-slate-600 w-16 capitalize">{type.replace('_', ' ')}</span>
                              <span className="text-xs font-medium w-10 text-right">{percent}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm text-slate-500 mb-2">Peak Posting Times</p>
                        <div className="space-y-1">
                          {contentAnalysis.posting_patterns?.peak_days?.map((day, i) => (
                            <div key={day.day} className="flex items-center justify-between text-sm">
                              <span className="text-slate-600">{day.day}</span>
                              <Badge variant="secondary" className="text-xs">{day.posts_percent}%</Badge>
                            </div>
                          ))}
                          {contentAnalysis.posting_patterns?.peak_hours?.map((hour, i) => (
                            <div key={hour.hour} className="flex items-center justify-between text-sm">
                              <span className="text-slate-600">{hour.hour}</span>
                              <Badge variant="outline" className="text-xs">Score: {hour.activity_score}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Hashtag Strategy */}
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium mb-2">Hashtag Strategy</p>
                      <div className="flex items-center gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Avg per post:</span>
                          <span className="font-medium ml-1">{contentAnalysis.hashtag_strategy?.avg_hashtags_per_post}</span>
                        </div>
                        <div className="flex gap-2">
                          {contentAnalysis.hashtag_strategy?.categories?.map(cat => (
                            <Badge key={cat.category} variant="outline" className="text-xs">
                              {cat.category}: {cat.count}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Caption Analysis */}
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium mb-2">Caption Style</p>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-slate-500">Style:</span>
                          <span className="font-medium ml-1 capitalize">{contentAnalysis.caption_analysis?.style}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Avg length:</span>
                          <span className="font-medium ml-1">{contentAnalysis.caption_analysis?.avg_length} chars</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Questions:</span>
                          <span className="font-medium ml-1">{contentAnalysis.caption_analysis?.question_frequency}</span>
                        </div>
                      </div>
                    </div>

                    {/* CTA Analysis */}
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <p className="text-sm font-medium mb-2">Call-to-Action Usage: {contentAnalysis.cta_analysis?.frequency}</p>
                      <div className="flex gap-2 flex-wrap">
                        {contentAnalysis.cta_analysis?.common_ctas?.map(cta => (
                          <Badge key={cta} variant="secondary" className="text-xs">{cta}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CompetitorsPage;
